import { css } from '@emotion/react';

import { COLORS } from '~/configs/theme.ts';

import type { SerializedStyles } from '@emotion/react';

type LoaderSize = 'small' | 'medium' | 'large' | 'minimum';

type LoaderThemeType = 'dark' | 'light';

export interface LoaderProps {
  size?: LoaderSize;
  theme?: LoaderThemeType;
  delay?: number;
  containerCss?: SerializedStyles | SerializedStyles[];
  fullScreen?: boolean;
}

interface ContainerStyleProps {
  width: string;
  height: string;
}

interface LoaderElementStyleProps {
  startColor: string;
  middleColor: string;
  endColor: string;
  startWidth: string;
  endWidth: string;
  delay: number;
}

const sizeStyles: {
  [key in LoaderSize]: {
    width: string;
    height: string;
    startWidth: string;
    endWidth: string;
  };
} = {
  minimum: {
    width: '16px',
    height: '16px',
    startWidth: '3.1px',
    endWidth: '4.6px',
  },
  small: {
    width: '20px',
    height: '20px',
    startWidth: '4px',
    endWidth: '6px',
  },
  medium: {
    width: '40px',
    height: '40px',
    startWidth: '8px',
    endWidth: '12px',
  },
  large: {
    width: '72px',
    height: '72px',
    startWidth: '12px',
    endWidth: '18px',
  },
};

const colorStyles: {
  [key in LoaderThemeType]: {
    startColor: string;
    middleColor: string;
    endColor: string;
  };
} = {
  dark: {
    startColor: COLORS.BLUE['100'],
    middleColor: COLORS.BLUE['200'],
    endColor: COLORS.BLUE['300'],
  },
  light: {
    startColor: COLORS.BLUE['50'],
    middleColor: COLORS.BLUE['100'],
    endColor: COLORS.BLUE['200'],
  },
};

const containerSize = {
  minimum: '14px',
  small: '17.5px',
  medium: '35px',
  large: '56px',
};

export default function Loader({
  size = 'medium',
  theme = 'dark',
  delay = 1,
  fullScreen,
  containerCss,
}: LoaderProps) {
  const { width, height, startWidth, endWidth } = sizeStyles[size];
  const { startColor, middleColor, endColor } = colorStyles[theme];

  return (
    <div
      css={[
        container({ width: fullScreen ? '100%' : width, height: fullScreen ? '100%' : height }),
        containerCss,
      ]}
      style={{ position: fullScreen ? 'fixed' : 'absolute' }}
    >
      <div css={innerContainer(size)}>
        <div
          css={leftElementSt({ startColor, startWidth, endColor, endWidth, middleColor, delay })}
        />
        <div
          css={middleElementSt({ startColor, startWidth, endColor, endWidth, middleColor, delay })}
        />
        <div
          css={rightElementSt({ startColor, startWidth, endColor, endWidth, middleColor, delay })}
        />
      </div>
    </div>
  );
}

const innerContainer = (size: LoaderSize) => css`
  display: flex;
  align-items: center;
  justify-content: space-between;

  width: ${containerSize[size]};
  height: ${containerSize[size]};
`;

const container = ({ width, height }: ContainerStyleProps) => css`
  display: flex;
  align-items: center;
  justify-content: center;

  width: ${width};
  height: ${height};
  z-index: 99999;
  inset: 0;
  background: rgba(255, 255, 255, 0.25);
`;

const leftElementSt = ({
  startColor,
  startWidth,
  endWidth,
  endColor,
  middleColor,
  delay,
}: LoaderElementStyleProps) => css`
  border-radius: 50%;

  @keyframes loading-left {
    0% {
      width: ${endWidth};
      height: ${endWidth};
      background: ${startColor};
    }

    33% {
      width: ${startWidth};
      height: ${startWidth};
      background: ${middleColor};
    }

    66% {
      width: ${startWidth};
      height: ${startWidth};
      background: ${endColor};
    }

    100% {
      width: ${endWidth};
      height: ${endWidth};
      background: ${startColor};
    }
  }

  animation: loading-left ${delay}s infinite ease-in;
`;

const middleElementSt = ({
  startColor,
  startWidth,
  endWidth,
  endColor,
  middleColor,
  delay,
}: LoaderElementStyleProps) => css`
  border-radius: 50%;

  @keyframes loading-middle {
    0% {
      width: ${startWidth};
      height: ${startWidth};
      background: ${endColor};
    }

    33% {
      width: ${endWidth};
      height: ${endWidth};
      background: ${startColor};
    }

    66% {
      width: ${startWidth};
      height: ${startWidth};
      background: ${middleColor};
    }

    100% {
      width: ${startWidth};
      height: ${startWidth};
      background: ${endColor};
    }
  }

  animation: loading-middle ${delay}s infinite ease-in;
`;

const rightElementSt = ({
  startColor,
  startWidth,
  endWidth,
  endColor,
  middleColor,
  delay,
}: LoaderElementStyleProps) => css`
  border-radius: 50%;

  @keyframes loading-right {
    0% {
      width: ${startWidth};
      height: ${startWidth};
      background: ${middleColor};
    }

    33% {
      width: ${startWidth};
      height: ${startWidth};
      background: ${endColor};
    }

    66% {
      width: ${endWidth};
      height: ${endWidth};
      background: ${startColor};
    }

    100% {
      width: ${startWidth};
      height: ${startWidth};
      background: ${middleColor};
    }
  }

  animation: loading-right ${delay}s infinite ease-in;
`;
